import { Link } from "react-router-dom";
import type { EventItem } from "@/types";
import StatusBadge from "@/components/base/StatusBadge";
import { formatDateShort, getWeekday } from "@/utils/format";

interface EventCardProps {
  event: EventItem;
  compact?: boolean;
}

export default function EventCard({ event, compact = false }: EventCardProps) {
  const isFinished = event.status === "finished";
  const homeWin = isFinished && (event.homeScore ?? 0) > (event.awayScore ?? 0);
  const awayWin = isFinished && (event.awayScore ?? 0) > (event.homeScore ?? 0);

  return (
    <Link
      to={`/event/${event.id}`}
      className="group block rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300 hover:bg-primary-50/40"
    >
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2 min-w-0">
          <span className="px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-900 text-[11px] font-label whitespace-nowrap">
            {event.subCategory}
          </span>
          <span className="text-[11px] text-foreground-500 whitespace-nowrap">· {event.level}</span>
        </div>
        <StatusBadge status={event.status} />
      </div>

      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 flex flex-col items-center gap-2 text-center">
          <div className="w-14 h-14 md:w-16 md:h-16 rounded-full overflow-hidden border border-background-200 bg-background-100">
            <img src={event.home.logo} alt={event.home.name} className="w-full h-full object-cover" />
          </div>
          <span className={`text-xs md:text-sm leading-tight ${homeWin ? "font-semibold text-foreground-950" : "text-foreground-700"}`}>
            {event.home.name}
          </span>
        </div>

        <div className="shrink-0 flex flex-col items-center">
          {event.status === "upcoming" ? (
            <>
              <span className="font-heading text-lg md:text-xl text-foreground-950">{event.time}</span>
              <span className="text-[11px] text-foreground-500 mt-0.5">未开始</span>
            </>
          ) : (
            <div className="flex items-center gap-2 font-heading text-2xl md:text-3xl">
              <span className={homeWin ? "text-primary-600" : "text-foreground-800"}>{event.homeScore}</span>
              <span className="text-foreground-300 text-lg">:</span>
              <span className={awayWin ? "text-primary-600" : "text-foreground-800"}>{event.awayScore}</span>
            </div>
          )}
        </div>

        <div className="flex-1 flex flex-col items-center gap-2 text-center">
          <div className="w-14 h-14 md:w-16 md:h-16 rounded-full overflow-hidden border border-background-200 bg-background-100">
            <img src={event.away.logo} alt={event.away.name} className="w-full h-full object-cover" />
          </div>
          <span className={`text-xs md:text-sm leading-tight ${awayWin ? "font-semibold text-foreground-950" : "text-foreground-700"}`}>
            {event.away.name}
          </span>
        </div>
      </div>

      {!compact ? (
        <div className="mt-5 pt-4 border-t border-background-100 flex items-center justify-between gap-2 text-xs text-foreground-500">
          <span className="flex items-center gap-1.5 min-w-0">
            <i className="ri-time-line text-sm" />
            <span className="whitespace-nowrap">
              {formatDateShort(event.date)} {getWeekday(event.date)} {event.time}
            </span>
          </span>
          <span className="hidden sm:flex items-center gap-1.5 min-w-0">
            <i className="ri-map-pin-line text-sm" />
            <span className="truncate max-w-[140px]">{event.venue}</span>
          </span>
        </div>
      ) : null}
    </Link>
  );
}